import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  ArrowRight,
  Zap,
  Mail,
  ChevronDown,
  Linkedin,
  MessageCircle,
  FileText,
  Sparkles,
  TrendingUp,
  Download,
  Undo2,
  CheckCircle2,
  BarChart3,
  Target,
} from "lucide-react";
import { Button } from "./ui/button";
import { Helmet } from "react-helmet";

// FAQ Component remains the same as it's independent of layout
const FAQ = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-700/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full py-4 text-left"
      >
        <span className="text-lg font-medium text-white">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        className="overflow-hidden"
      >
        <p className="pb-4 text-gray-400">{answer}</p>
      </motion.div>
    </div>
  );
};

// Feature highlight card
const FeatureHighlight = ({ icon: Icon, title, description, gradient }) => (
  <motion.div
    whileHover={{ y: -5, scale: 1.02 }}
    className="p-6 bg-gray-800/30 border border-gray-700/50 rounded-xl backdrop-blur-sm hover:border-blue-500/30 transition-all duration-300"
  >
    <div className={`w-12 h-12 ${gradient} rounded-xl flex items-center justify-center mb-4`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-400 text-sm">{description}</p>
  </motion.div>
);

export const LandingPage = () => {
  const navigate = useNavigate();

  const openApp = () => {
    window.open(
      process.env.REACT_APP_FRONTEND_APP_URL,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const faqs = [
    {
      question: "How does the AI interviewer work?",
      answer:
        "Our AI interviewer uses advanced natural language processing to conduct realistic interview sessions, adapting questions based on your responses and experience level.",
    },
    {
      question: "What is Resume Chat?",
      answer:
        "Resume Chat is our new AI-powered feature that lets you enhance your resume through conversation. Simply chat with AI to add skills, improve descriptions, and optimize your resume - all with real-time preview, version history, and instant PDF export.",
    },
    {
      question: "What types of interviews are supported?",
      answer:
        "We support all types of interviews but we are currently specialized in software engineering and other technical roles.",
    },
    {
      question: "Do we have to pay anything?",
      answer:
        "No! You can try AI Interviewer for free for now and will be free until my funds last🙂. Premium features might be added in the future.",
    },
    {
      question: "How long does an interview session last?",
      answer:
        "There are three types of interview session, Quick assessment, Standard interview and In-depth evaluation. Quick assessment is 5 minutes, Standard interview is 10 minutes and In-depth evaluation is 20 minutes long.",
    },
  ];

  const mainFeatures = [
    {
      icon: Target,
      title: "AI Interviews",
      description: "Practice with realistic AI-powered interview questions tailored to your role.",
      gradient: "bg-gradient-to-r from-blue-500 to-cyan-500",
    },
    {
      icon: MessageCircle,
      title: "Resume Chat",
      description: "Enhance your resume through AI conversation with real-time updates.",
      gradient: "bg-gradient-to-r from-green-500 to-emerald-500",
    },
    {
      icon: BarChart3,
      title: "Analytics",
      description: "Get detailed insights and scores to track your improvement.",
      gradient: "bg-gradient-to-r from-purple-500 to-pink-500",
    },
  ];

  return (
    <div className="text-white">
      <Helmet>
        <title>Sakshatkar AI - Master Tech Interviews & Perfect Your Resume</title>
        <meta
          name="description"
          content="Sakshatkar AI is an AI-powered platform for interview practice and resume optimization. Practice interviews, enhance your resume with AI chat, and get personalized feedback."
        />
        <meta property="og:title" content="Sakshatkar AI - Master Tech Interviews" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-24 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-8 max-w-5xl mx-auto px-4"
        >
          <h1 className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 leading-tight">
            Master Your Tech Interviews
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Practice with AI-powered interviews, enhance your resume through chat, 
            and get personalized feedback to land your dream job.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => openApp()}
              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white px-8 py-6 text-lg"
            >
              Start Practicing
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => navigate("/features")}
              variant="outline"
              className="border-gray-600 hover:bg-gray-800 text-white px-8 py-6 text-lg"
            >
              View Features
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Main Features Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {mainFeatures.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <FeatureHighlight {...feature} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Resume Chat Feature Highlight */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-2 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-cyan-500/10 rounded-3xl blur-2xl" />
            <div className="relative p-8 lg:p-12 rounded-2xl bg-gray-800/50 border border-green-500/20 backdrop-blur-xl">
              <div className="flex flex-col lg:flex-row gap-12 items-center">
                {/* Content */}
                <div className="flex-1 space-y-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                    <Sparkles className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-semibold text-green-300">NEW FEATURE</span>
                  </div>

                  <h2 className="text-4xl lg:text-5xl font-bold text-white">
                    Resume Chat
                  </h2>
                  
                  <p className="text-xl text-gray-300 leading-relaxed">
                    Transform your resume through conversation. Chat with AI to add skills, 
                    improve descriptions, and optimize for ATS - all with real-time updates 
                    you can see instantly.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    {[
                      { icon: FileText, text: "Real-time resume preview" },
                      { icon: TrendingUp, text: "ATS score tracking" },
                      { icon: Undo2, text: "Version history & rollback" },
                      { icon: Download, text: "Instant PDF export" },
                      { icon: Sparkles, text: "AI content suggestions" },
                      { icon: CheckCircle2, text: "Highlighted changes" },
                    ].map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + idx * 0.1 }}
                        className="flex items-center gap-3"
                      >
                        <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                          <item.icon className="w-4 h-4 text-green-400" />
                        </div>
                        <span className="text-gray-300">{item.text}</span>
                      </motion.div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openApp}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/25 transition-all duration-300"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Try Resume Chat Free
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </div>

                {/* Visual Demo */}
                <div className="lg:w-96 w-full">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="relative"
                  >
                    <div className="absolute -inset-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl" />
                    <div className="relative p-6 rounded-2xl bg-gray-900/95 border border-gray-700/50 shadow-2xl">
                      {/* Chat Messages Demo */}
                      <div className="space-y-4 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                          <div className="px-4 py-2.5 bg-gray-700/50 rounded-xl rounded-tl-none text-sm text-gray-200">
                            I can help enhance your resume! What would you like to improve?
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3 justify-end">
                          <div className="px-4 py-2.5 bg-green-500/20 border border-green-500/30 rounded-xl rounded-tr-none text-sm text-gray-100">
                            Add Python and make my experience more impactful
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                          <div className="px-4 py-2.5 bg-gray-700/50 rounded-xl rounded-tl-none text-sm text-gray-200">
                            <span className="text-green-400">✓ Done!</span> Added Python to skills and enhanced your work experience with action verbs and metrics.
                          </div>
                        </div>
                      </div>

                      {/* Resume Preview */}
                      <div className="border-t border-gray-700/50 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-400" />
                            <span className="text-xs text-gray-400">Live Preview</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-green-400">v4</span>
                            <span className="text-xs text-gray-500">|</span>
                            <span className="text-xs text-blue-400">Score: 87</span>
                          </div>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg space-y-2">
                          <div className="h-2 w-28 bg-gray-600 rounded" />
                          <div className="h-1.5 w-full bg-gray-700 rounded" />
                          <div className="h-1.5 w-3/4 bg-gray-700 rounded" />
                          <div className="flex gap-1.5 pt-1">
                            <span className="px-2 py-0.5 text-[10px] bg-green-500/30 text-green-300 rounded border border-green-500/30 animate-pulse">
                              Python ✨
                            </span>
                            <span className="px-2 py-0.5 text-[10px] bg-blue-500/30 text-blue-300 rounded">JavaScript</span>
                            <span className="px-2 py-0.5 text-[10px] bg-purple-500/30 text-purple-300 rounded">React</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Interview Feature */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                <Bot className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white">
              AI-Powered Interview Practice
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Get started with personalized interview sessions tailored to your
              experience level. Practice with our AI interviewer and receive
              instant feedback to improve your skills.
            </p>
            <Button
              onClick={() => navigate("/features")}
              variant="outline"
              className="mt-8 border-blue-500/20 hover:bg-blue-500/10"
            >
              Explore All Features
              <Zap className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQ key={index} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-6">Get in Touch</h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            Have questions about Sakshatkar AI? We'd love to hear from you.
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {/* Email */}
            <motion.a
              href="mailto:contact@sakshatkarai.com"
              whileHover={{ scale: 1.05 }}
              className="flex flex-col items-center p-6 bg-gray-800/50 rounded-lg border border-gray-700/50 backdrop-blur-sm"
            >
              <Mail className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="font-medium mb-2">Email Us</h3>
              <p className="text-gray-400 text-center">
                contact@sakshatkarai.com
              </p>
            </motion.a>

            {/* LinkedIn */}
            <motion.a
              href="https://www.linkedin.com/in/mitanshu-dodia-34b287208/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              className="flex flex-col items-center p-6 bg-gray-800/50 rounded-lg border border-gray-700/50 backdrop-blur-sm"
            >
              <Linkedin className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="font-medium mb-2">Connect on LinkedIn</h3>
              <p className="text-gray-400 text-center">Let's connect!</p>
            </motion.a>
          </div>
        </div>
      </section>
    </div>
  );
};
